import { useCallback, useMemo, useState } from 'react'
import { Upload, FileSpreadsheet, FileText, AlertTriangle, Check, X } from 'lucide-react'
import { SmartBack } from '../navigation/SmartBack.js'
import { adminMutate, adminUpload, usePermissions, useToast, Select } from '../admin/primitives.js'
import { Field, TextInput } from '../admin/form.js'
import { useConfirm } from '../shell/confirm.js'
import { formatMoney } from './money.js'
import {
  useRessource, EtatBloc, estObjet, texte, entier, cents,
  MotifChamp, motifValide,
} from './RevenueShared.js'
import './finance.css'
import './finance-revenue.css'
import './finance-import.css'

/**
 * IMPORT HISTORIQUE — L'ASSISTANT EN HUIT ÉTAPES.
 *
 * ═══ LA RÈGLE QUI COMMANDE CET ÉCRAN ═══
 *
 * DÉPOSER NE MODIFIE AUCUNE FINANCE. Chaque étape le répète, parce que c'est
 * ce qu'une personne a besoin de savoir avant de cliquer : le fichier est
 * conservé, lu, prévisualisé — et rien n'entre dans le chiffre d'affaires tant
 * qu'un humain n'a pas validé. Dans ce lot, la validation REFUSE : le chemin
 * d'écriture n'est pas ouvert. L'écran le dit en toutes lettres plutôt que de
 * griser un bouton, qui se lirait « il vous manque un droit ».
 *
 * ═══ POURQUOI L'ÉTAPE « CORRESPONDANCE » EXISTE ═══
 *
 * Le classeur de clôture est une grille adressable : ce qu'on y lit est sûr.
 * Les deux exports PDF de feuilles Google ne le sont pas — une colonne n'y a
 * aucun lien structurel avec son en-tête. L'écran PROPOSE les en-têtes lus et
 * leur abscisse ; c'est la personne qui tranche. Tout ce qu'elle n'a pas
 * tranché reste « non compris », et c'est une réponse acceptable.
 *
 * ═══ CE QUI N'EST PAS ICI ═══
 *
 * Cet écran ne fabrique pas sa propre copie du fichier : il passe par la route
 * de dépôt commune, celle qui hache et dédoublonne par le contenu. C'est ce
 * lien qui fait qu'un aperçu peut, dans deux ans, être rapproché de l'octet
 * exact dont il vient.
 */

/* ═════════════════════════ le vocabulaire de l'écran ═════════════════════════ */

type Etape = 'fichier' | 'detection' | 'periodes' | 'correspondance' | 'apercu' | 'conflits' | 'validation' | 'import'

const ETAPES: Array<{ cle: Etape; label: string }> = [
  { cle: 'fichier', label: 'Fichier' },
  { cle: 'detection', label: 'Détection' },
  { cle: 'periodes', label: 'Périodes' },
  { cle: 'correspondance', label: 'Correspondance' },
  { cle: 'apercu', label: 'Aperçu' },
  { cle: 'conflits', label: 'Conflits' },
  { cle: 'validation', label: 'Validation' },
  { cle: 'import', label: 'Import' },
]

type Format = 'CLASSEUR_CLOTURE' | 'SUIVI_CA' | 'CA_ANNUEL'

const FORMATS: Record<Format, string> = {
  CLASSEUR_CLOTURE: 'Classeur de clôture (tableur)',
  SUIVI_CA: 'Suivi quotidien des recettes (export PDF)',
  CA_ANNUEL: 'Exercice annuel (export PDF)',
}

type Confiance = 'CONFIRMED' | 'PROBABLE' | 'UNMAPPED'

const CONFIANCES: Record<Confiance, { label: string; pill: string }> = {
  CONFIRMED: { label: 'Confirmé', pill: 'lc-pill--ok' },
  PROBABLE: { label: 'À confirmer', pill: 'lc-pill--warning' },
  UNMAPPED: { label: 'Non compris', pill: 'lc-pill--idle' },
}

/**
 * Ce qu'une colonne peut désigner : les codes de composante de recette DÉJÀ en
 * base. On ne crée pas un second vocabulaire pour l'import, sans quoi deux
 * écrans finiraient par nommer différemment la même somme.
 */
const CHAMPS = [
  { value: '', label: 'Ne pas reprendre cette colonne' },
  { value: 'TICKETING_ONLINE', label: 'Billetterie en ligne' },
  { value: 'TICKETING_ONSITE', label: 'Billetterie sur place' },
  { value: 'BAR', label: 'Bar' },
  { value: 'VESTIAIRE', label: 'Vestiaire' },
  { value: 'OFFERS', label: 'Offerts' },
  { value: 'PROVIDER_FEES', label: 'Frais fournisseur' },
  { value: 'OTHER', label: 'Autre' },
]

/* ═════════════════════════ ce que l'API rend ═════════════════════════ */

interface DocumentSource {
  id: string
  filename: string
  received_at: string
}

interface LigneLot {
  id: string
  origine: string
  business_date: string | null
  confiance: Confiance
  conflit: string | null
  note: string | null
  payload: Record<string, unknown>
}

interface LotDetail {
  id: string
  document_filename: string
  format: Format | null
  parser_version: string
  status: string
  committed_at: string | null
  row_count: number
  conflict_count: number
  unmapped_count: number
  mapping: Record<string, unknown>
  summary: Record<string, unknown>
  lignes: LigneLot[]
}

/**
 * Les validateurs rendent `null` dès que la charge ne correspond pas au
 * contrat : une réponse d'une autre forme devient une ABSENCE honnête, jamais
 * un écran à zéro.
 */
function lireDocuments(v: unknown): DocumentSource[] | null {
  if (!estObjet(v)) return null
  const brut = v['documents']
  if (!Array.isArray(brut)) return null
  const out: DocumentSource[] = []
  for (const d of brut) {
    if (!estObjet(d)) continue
    const id = texte(d['id'])
    const filename = texte(d['filename'])
    if (id === null || filename === null) continue
    out.push({ id, filename, received_at: texte(d['received_at']) ?? '' })
  }
  return out
}

function lireLot(v: unknown): LotDetail | null {
  if (!estObjet(v)) return null
  const id = texte(v['id'])
  if (id === null) return null
  const brut = Array.isArray(v['lignes']) ? v['lignes'] : []
  const lignes: LigneLot[] = []
  for (const l of brut) {
    if (!estObjet(l)) continue
    const lid = texte(l['id'])
    const origine = texte(l['origine'])
    if (lid === null || origine === null) continue
    const c = texte(l['confiance'])
    lignes.push({
      id: lid, origine,
      business_date: texte(l['business_date']),
      confiance: c === 'CONFIRMED' || c === 'PROBABLE' ? c : 'UNMAPPED',
      conflit: texte(l['conflit']),
      note: texte(l['note']),
      payload: estObjet(l['payload']) ? l['payload'] : {},
    })
  }
  const f = texte(v['format'])
  return {
    id,
    document_filename: texte(v['document_filename']) ?? '—',
    format: f === 'CLASSEUR_CLOTURE' || f === 'SUIVI_CA' || f === 'CA_ANNUEL' ? f : null,
    parser_version: texte(v['parser_version']) ?? '—',
    status: texte(v['status']) ?? 'DRAFT',
    committed_at: texte(v['committed_at']),
    row_count: entier(v['row_count']) ?? lignes.length,
    conflict_count: entier(v['conflict_count']) ?? 0,
    unmapped_count: entier(v['unmapped_count']) ?? 0,
    mapping: estObjet(v['mapping']) ? v['mapping'] : {},
    summary: estObjet(v['summary']) ? v['summary'] : {},
    lignes,
  }
}

/* ═════════════════════════ l'écran ═════════════════════════ */

export function FinanceImportPage() {
  const { permissions, loading } = usePermissions()
  const peutImporter = !loading && permissions.includes('finance.history.import')
  const peutDeposer = !loading && permissions.includes('finance.source.manage')

  const [etape, setEtape] = useState<Etape>('fichier')
  const [documentId, setDocumentId] = useState('')
  const [lotId, setLotId] = useState<string | null>(null)
  const [annee, setAnnee] = useState('')
  const [colonnes, setColonnes] = useState<Record<string, string>>({})
  const [motif, setMotif] = useState('')
  const [refusValidation, setRefusValidation] = useState<string | null>(null)
  const [envoi, setEnvoi] = useState(false)

  const toast = useToast()
  const confirmer = useConfirm()

  const documents = useRessource<DocumentSource[]>(
    peutImporter ? '/api/v1/finance/sources?limit=100' : null, lireDocuments,
  )
  const lot = useRessource<LotDetail>(
    lotId === null ? null : `/api/v1/finance/history/batches/${lotId}`, lireLot,
  )
  const detail = lot.etat.phase === 'ok' ? lot.etat.data : null

  /* ── étape 2 : ouvrir le lot, donc lire le fichier ── */
  const detecter = useCallback(async () => {
    if (documentId === '') return
    setEnvoi(true)
    const corps: Record<string, unknown> = { document_id: documentId }
    if (/^\d{4}$/.test(annee)) corps['annee'] = Number(annee)
    const r = await adminMutate<{ error?: string; message?: string; id?: string; format?: string }>(
      'POST', '/api/v1/finance/history/batches', corps,
    )
    setEnvoi(false)
    if (r.status >= 200 && r.status < 300 && r.data.id !== undefined) {
      setLotId(r.data.id)
      setEtape('detection')
      toast('ok', 'Fichier lu. Aucun chiffre n’a été modifié.')
      return
    }
    toast('error', r.data.message ?? 'Ce fichier n’a pas pu être lu.')
  }, [documentId, annee, toast])

  /* ── étapes 3 et 4 : ce qu'un humain DÉCLARE ── */
  const enregistrerCorrespondance = useCallback(async (suivante: Etape) => {
    if (lotId === null) return
    setEnvoi(true)
    const corps: Record<string, unknown> = {
      colonnes: Object.entries(colonnes)
        .filter(([, champ]) => champ !== '')
        .map(([x, champ]) => ({ x: Number(x), champ })),
    }
    if (/^\d{4}$/.test(annee)) corps['annee'] = Number(annee)
    const r = await adminMutate<{ error?: string; message?: string }>(
      'POST', `/api/v1/finance/history/batches/${lotId}/mapping`, corps,
    )
    setEnvoi(false)
    if (r.status >= 200 && r.status < 300) {
      lot.recharger()
      setEtape(suivante)
      return
    }
    toast('error', r.data.message ?? 'La correspondance n’a pas pu être enregistrée.')
  }, [lotId, colonnes, annee, lot, toast])

  /* ── étape 7 : la validation, qui refuse dans ce lot ── */
  const valider = useCallback(async () => {
    if (lotId === null || !motifValide(motif)) return
    const ok = await confirmer({
      title: 'Écrire cet historique dans la comptabilité ?',
      message: 'Les montants repris deviendraient des chiffres de Gestion. Une reprise d’historique ne se refait pas : relisez l’aperçu avant.',
      details: [
        { label: 'Lignes', value: String(detail?.row_count ?? 0) },
        { label: 'Conflits', value: String(detail?.conflict_count ?? 0) },
      ],
      variant: 'critical',
      confirmLabel: 'Écrire',
    })
    if (!ok) return
    setEnvoi(true)
    const r = await adminMutate<{ error?: string; message?: string }>(
      'POST', `/api/v1/finance/history/batches/${lotId}/commit`, { reason: motif },
    )
    setEnvoi(false)
    setEtape('import')
    if (r.status >= 200 && r.status < 300) {
      setRefusValidation(null)
      lot.recharger()
      return
    }
    setRefusValidation(r.data.message ?? 'Le serveur a refusé cette validation.')
  }, [lotId, motif, detail, confirmer, lot])

  /* ── le dépôt : la MÊME porte que l'écran Sources ── */
  const deposer = useCallback(async (f: File) => {
    setEnvoi(true)
    // Le type annoncé par le poste n'est qu'un INDICE : l'analyseur du serveur
    // n'accepte que `application/octet-stream`, et c'est LUI qui reconnaît le
    // format sur les octets.
    const octets = new File([f], f.name, { type: 'application/octet-stream' })
    const q = new URLSearchParams({
      filename: f.name,
      provider: 'MANUEL',
      mime: f.type === '' ? 'application/octet-stream' : f.type,
    })
    const r = await adminUpload<{ error?: string; message?: string; id?: string; duplicate?: boolean }>(
      'POST', `/api/v1/finance/sources?${q.toString()}`, octets,
    )
    setEnvoi(false)
    if (r.status >= 200 && r.status < 300 && r.data.id !== undefined) {
      setDocumentId(r.data.id)
      documents.recharger()
      toast('ok', r.data.duplicate === true
        ? 'Ce fichier était déjà déposé : c’est celui-là qui sera lu.'
        : 'Fichier déposé. Aucun chiffre n’a été modifié.')
      return
    }
    toast('error', r.data.message ?? 'Ce fichier n’a pas pu être déposé.')
  }, [documents, toast])

  const optionsDocuments = useMemo(() => {
    const liste = documents.etat.phase === 'ok' ? documents.etat.data : []
    return [
      { value: '', label: 'Choisir un document déposé' },
      ...liste.map((d) => ({ value: d.id, label: `${d.filename} — reçu le ${d.received_at.slice(0, 10)}` })),
    ]
  }, [documents.etat])

  const entetes = useMemo(() => lireEntetes(detail), [detail])

  return (
    <main className="lc-page lc-page--large lc-stagger">
      <SmartBack />

      <header className="fim-hero">
        <span className="fim-hero__icon" aria-hidden="true"><Upload size={26} /></span>
        <div>
          <h1 className="lc-title">Import historique</h1>
          <p className="lc-subtitle">
            Reprendre un classeur de clôture ou un export de suivi, ligne à ligne, sans rien
            écrire tant que personne n’a validé.
          </p>
        </div>
      </header>

      <p className="fi-reserve fim-doctrine">
        <strong>Déposer ne modifie aucun chiffre.</strong> Le fichier est conservé, lu et
        prévisualisé. Rien n’entre dans la comptabilité avant une validation humaine explicite —
        et dans cette version, cette validation est encore fermée.
      </p>

      <ol className="fim-steps" aria-label="Étapes de l’import">
        {ETAPES.map((e, i) => (
          <li
            key={e.cle}
            className={`fim-step${e.cle === etape ? ' fim-step--on' : ''}${estFaite(e.cle, etape) ? ' fim-step--done' : ''}`}
          >
            <span className="fim-step__n" aria-hidden="true">{i + 1}</span>
            <span className="fim-step__l">{e.label}</span>
          </li>
        ))}
      </ol>

      {!peutImporter && !loading ? (
        <section className="fi-empty">
          <p className="fi-empty__title">Votre rôle ne donne pas accès à la reprise d’historique.</p>
        </section>
      ) : null}

      {peutImporter && etape === 'fichier' ? (
        <section className="lc-panel fim-panel">
          <div className="lc-panel__head"><h2>1 · Le fichier</h2></div>
          <div className="lc-panel__body">
            <EtatBloc etat={documents.etat} quoi="les documents déposés" />
            <Field label="Document déjà déposé" helper="Les fichiers arrivés par dépôt manuel ou par courriel.">
              <Select
                value={documentId} options={optionsDocuments} onChange={setDocumentId}
                ariaLabel="Document source à reprendre"
              />
            </Field>
            {peutDeposer ? (
              <div className="fim-upload">
                <input
                  type="file" className="fim-upload__input"
                  aria-label="Déposer un classeur ou un export PDF"
                  onChange={(ev) => {
                    const f = ev.target.files?.[0]
                    if (f) void deposer(f)
                    ev.target.value = ''
                  }}
                />
                <p className="fr-mini">
                  Le fichier est haché à l’arrivée : le déposer deux fois ne le duplique pas.
                </p>
              </div>
            ) : null}
            <div className="fr-actions fr-actions--end">
              <button
                className="lc-btn lc-btn--primary" type="button"
                disabled={documentId === '' || envoi}
                onClick={() => { void detecter() }}
              >Lire ce fichier</button>
            </div>
          </div>
        </section>
      ) : null}

      {peutImporter && etape !== 'fichier' ? (
        <>
          <EtatBloc etat={lot.etat} quoi="le lot d’import" />
          {detail ? (
            <VueLot
              etape={etape} lot={detail} entetes={entetes}
              annee={annee} setAnnee={setAnnee}
              colonnes={colonnes} setColonnes={setColonnes}
              motif={motif} setMotif={setMotif}
              refus={refusValidation} envoi={envoi}
              aller={setEtape}
              enregistrerCorrespondance={enregistrerCorrespondance}
              valider={valider}
            />
          ) : null}
        </>
      ) : null}
    </main>
  )
}

/* ═════════════════════════ les étapes 2 à 8 ═════════════════════════ */

interface EnTeteLu { x: number; texte: string }

/**
 * Les en-têtes proposés à l'étape de correspondance. Un même libellé peut être
 * peint plusieurs fois (le bandeau se répète de page en page) : une seule
 * proposition par abscisse suffit, et deux propositions identiques feraient
 * croire à deux colonnes.
 */
function lireEntetes(lot: LotDetail | null): EnTeteLu[] {
  if (!lot) return []
  const brut = lot.summary['entetes']
  if (!Array.isArray(brut)) return []
  const out: EnTeteLu[] = []
  for (const e of brut) {
    if (!estObjet(e)) continue
    const t = texte(e['texte'])
    const x = cents(e['x'])
    if (t === null || x === null) continue
    if (out.some((o) => o.x === x)) continue
    out.push({ x, texte: t })
  }
  return out.sort((a, b) => a.x - b.x)
}

function VueLot(props: {
  etape: Etape
  lot: LotDetail
  entetes: EnTeteLu[]
  annee: string
  setAnnee: (v: string) => void
  colonnes: Record<string, string>
  setColonnes: (v: Record<string, string>) => void
  motif: string
  setMotif: (v: string) => void
  refus: string | null
  envoi: boolean
  aller: (e: Etape) => void
  enregistrerCorrespondance: (suivante: Etape) => Promise<void>
  valider: () => Promise<void>
}) {
  const { lot, etape } = props
  const conflits = lot.lignes.filter((l) => l.conflit !== null)
  const reserves = Array.isArray(lot.summary['reserves']) ? (lot.summary['reserves'] as unknown[]) : []

  return (
    <>
      {etape === 'detection' ? (
        <section className="lc-panel fim-panel">
          <div className="lc-panel__head"><h2>2 · Ce que Gestion a reconnu</h2></div>
          <div className="lc-panel__body">
            <dl className="fim-facts">
              <div><dt>Fichier</dt><dd>{lot.document_filename}</dd></div>
              <div><dt>Format</dt><dd>{lot.format === null ? 'Non reconnu' : FORMATS[lot.format]}</dd></div>
              <div><dt>Lecteur</dt><dd className="fim-code">{lot.parser_version}</dd></div>
              <div><dt>Lignes lues</dt><dd>{lot.row_count}</dd></div>
            </dl>
            <ResumeFormat lot={lot} />
            <Reserves items={reserves} />
            <div className="fr-actions fr-actions--end">
              <button className="lc-btn" type="button" onClick={() => props.aller('periodes')}>Continuer</button>
            </div>
          </div>
        </section>
      ) : null}

      {etape === 'periodes' ? (
        <section className="lc-panel fim-panel">
          <div className="lc-panel__head"><h2>3 · Les périodes</h2></div>
          <div className="lc-panel__body">
            {lot.format === 'SUIVI_CA' ? (
              <>
                <p className="fr-note">
                  Ce document écrit « 19 mars », jamais l’année. Sans elle, aucune ligne ne peut
                  être rattachée à une nuit — et la deviner rattacherait une soirée de septembre à
                  la mauvaise saison.
                </p>
                <Field label="Année de cette saison" helper="Quatre chiffres. C’est vous qui la déclarez.">
                  <TextInput
                    value={props.annee} onChange={props.setAnnee} maxLength={4}
                    placeholder="2026" ariaLabel="Année de la saison reprise"
                  />
                </Field>
              </>
            ) : (
              <p className="fr-note">
                Les périodes de ce document sont écrites dedans : elles ont été lues telles quelles.
              </p>
            )}
            <PeriodesLues lot={lot} />
            <div className="fr-actions fr-actions--end">
              <button className="lc-btn" type="button" onClick={() => props.aller('detection')}>Étape précédente</button>
              <button
                className="lc-btn lc-btn--primary" type="button" disabled={props.envoi}
                onClick={() => { void props.enregistrerCorrespondance('correspondance') }}
              >Continuer</button>
            </div>
          </div>
        </section>
      ) : null}

      {etape === 'correspondance' ? (
        <section className="lc-panel fim-panel">
          <div className="lc-panel__head"><h2>4 · La correspondance</h2></div>
          <div className="lc-panel__body">
            {lot.format === 'CLASSEUR_CLOTURE' ? (
              <p className="fr-note">
                Ce classeur est une grille adressable : chaque montant est repéré à la cellule
                près, feuille et colonne comprises. Il n’y a rien à faire correspondre.
              </p>
            ) : (
              <>
                <p className="fr-note">
                  Dans un export PDF, une colonne n’a aucun lien avec son en-tête : seule
                  l’abscisse les rapproche. Gestion propose ce qu’elle a lu ; c’est vous qui dites
                  ce que chaque colonne contient. Ce que vous ne dites pas reste « non compris ».
                </p>
                <div className="fi-tablewrap">
                  <table className="fi-table fim-map">
                    <thead>
                      <tr>
                        <th scope="col">En-tête lu</th>
                        <th scope="col">Position</th>
                        <th scope="col">Ce que la colonne contient</th>
                      </tr>
                    </thead>
                    <tbody>
                      {props.entetes.map((e) => (
                        <tr key={`${e.x}-${e.texte}`}>
                          <td>{e.texte}</td>
                          <td className="fim-code">x&nbsp;{e.x}</td>
                          <td>
                            <Select
                              value={props.colonnes[String(e.x)] ?? ''}
                              options={CHAMPS}
                              onChange={(v) => props.setColonnes({ ...props.colonnes, [String(e.x)]: v })}
                              ariaLabel={`Champ de la colonne « ${e.texte} »`}
                              compact
                            />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
            <div className="fr-actions fr-actions--end">
              <button className="lc-btn" type="button" onClick={() => props.aller('periodes')}>Étape précédente</button>
              <button
                className="lc-btn lc-btn--primary" type="button" disabled={props.envoi}
                onClick={() => { void props.enregistrerCorrespondance('apercu') }}
              >Voir l’aperçu</button>
            </div>
          </div>
        </section>
      ) : null}

      {etape === 'apercu' ? (
        <section className="lc-panel fim-panel">
          <div className="lc-panel__head">
            <h2>5 · L’aperçu — exactement ce qui serait créé</h2>
            <span className="lc-pill lc-pill--idle">{lot.row_count} ligne(s)</span>
          </div>
          <div className="lc-panel__body">
            <TableApercu lignes={lot.lignes} />
            <div className="fr-actions fr-actions--end">
              <button className="lc-btn" type="button" onClick={() => props.aller('correspondance')}>Étape précédente</button>
              <button className="lc-btn lc-btn--primary" type="button" onClick={() => props.aller('conflits')}>
                Voir les conflits
              </button>
            </div>
          </div>
        </section>
      ) : null}

      {etape === 'conflits' ? (
        <section className="lc-panel fim-panel">
          <div className="lc-panel__head">
            <h2>6 · Les conflits</h2>
            {conflits.length > 0
              ? <span className="lc-pill lc-pill--warning">{conflits.length}</span>
              : <span className="lc-pill lc-pill--ok">Aucun</span>}
          </div>
          <div className="lc-panel__body">
            {conflits.length === 0 ? (
              <p className="fr-note">Aucune nuit de ce fichier n’est déjà arrêtée dans Gestion.</p>
            ) : (
              <>
                <p className="fr-note fr-note--warn">
                  Une nuit déjà arrêtée ne s’écrase pas. Ces lignes décrivent le même fait que la
                  clôture existante : c’est à vous de dire laquelle fait foi.
                </p>
                <ul className="fim-conflits">
                  {conflits.map((c) => (
                    <li key={c.id}>
                      <span className="fim-conflits__icon" aria-hidden="true"><AlertTriangle size={16} /></span>
                      <div>
                        <p className="fim-conflits__t">{c.business_date ?? 'Nuit inconnue'} — {c.origine}</p>
                        <p className="fr-mini">{c.note}</p>
                      </div>
                    </li>
                  ))}
                </ul>
              </>
            )}
            <div className="fr-actions fr-actions--end">
              <button className="lc-btn" type="button" onClick={() => props.aller('apercu')}>Étape précédente</button>
              <button className="lc-btn lc-btn--primary" type="button" onClick={() => props.aller('validation')}>
                Continuer
              </button>
            </div>
          </div>
        </section>
      ) : null}

      {etape === 'validation' ? (
        <section className="lc-panel fim-panel">
          <div className="lc-panel__head"><h2>7 · La validation</h2></div>
          <div className="lc-panel__body">
            <p className="fi-reserve">
              Jusqu’ici, rien n’a été écrit : le lot est un brouillon, et l’aperçu dit ce qui
              serait créé. La validation est le seul geste qui ferait entrer ces montants dans la
              comptabilité.
            </p>
            <MotifChamp
              valeur={props.motif} onChange={props.setMotif}
              label="Pourquoi cette reprise"
              aide="Douze caractères au moins. C’est la seule trace qui restera de la décision."
            />
            <div className="fr-actions fr-actions--end">
              <button className="lc-btn" type="button" onClick={() => props.aller('conflits')}>Étape précédente</button>
              <button
                className="lc-btn lc-btn--danger" type="button"
                disabled={!motifValide(props.motif) || props.envoi}
                onClick={() => { void props.valider() }}
              >Écrire dans la comptabilité</button>
            </div>
          </div>
        </section>
      ) : null}

      {etape === 'import' ? (
        <section className="lc-panel fim-panel">
          <div className="lc-panel__head"><h2>8 · L’import</h2></div>
          <div className="lc-panel__body">
            {props.refus !== null ? (
              <div className="fim-verdict fim-verdict--refus">
                <span aria-hidden="true"><X size={20} /></span>
                <div>
                  <p className="fim-verdict__t">Rien n’a été écrit.</p>
                  <p>{props.refus}</p>
                  <p className="fr-mini">
                    L’aperçu reste consultable : il dit ce qui aurait été créé, et il fait foi.
                  </p>
                </div>
              </div>
            ) : (
              <div className="fim-verdict fim-verdict--ok">
                <span aria-hidden="true"><Check size={20} /></span>
                <div>
                  <p className="fim-verdict__t">Lot importé.</p>
                  <p className="fr-mini">Importé le {lot.committed_at ?? '—'}.</p>
                </div>
              </div>
            )}
            <div className="fr-actions fr-actions--end">
              <button className="lc-btn" type="button" onClick={() => props.aller('apercu')}>Revoir l’aperçu</button>
            </div>
          </div>
        </section>
      ) : null}
    </>
  )
}

/* ═════════════════════════ briques d'affichage ═════════════════════════ */

function ResumeFormat({ lot }: { lot: LotDetail }) {
  const s = lot.summary
  if (lot.format === 'CLASSEUR_CLOTURE') {
    return (
      <p className="fr-mini">
        <FileSpreadsheet size={14} aria-hidden="true" />{' '}
        {entier(s['feuilles_nuit']) ?? 0} feuille(s) nuit, dont {entier(s['nuits_avec_donnees']) ?? 0} portent
        réellement des valeurs. Les autres n’ont jamais été tenues : elles ne sont pas reprises.
      </p>
    )
  }
  if (lot.format === 'SUIVI_CA') {
    return (
      <p className="fr-mini">
        <FileText size={14} aria-hidden="true" />{' '}
        {entier(s['pages']) ?? 0} page(s), {entier(s['lignes_retenues']) ?? 0} ligne(s) retenue(s) ;{' '}
        {entier(s['journees_sans_mouvement']) ?? 0} journée(s) sans le moindre mouvement ont été écartées.
      </p>
    )
  }
  return (
    <p className="fr-mini">
      <FileText size={14} aria-hidden="true" />{' '}
      {entier(s['pages']) ?? 0} page(s), {entier(s['lignes_mensuelles']) ?? 0} ligne(s) mensuelle(s)
      et {entier(s['lignes_cumul']) ?? 0} cumul(s) de fin d’année.
    </p>
  )
}

/** Ce que le document NE PERMET PAS d'affirmer, affiché tel que le lecteur l'a dit. */
function Reserves({ items }: { items: unknown[] }) {
  const phrases = items.map((x) => texte(x)).filter((x): x is string => x !== null)
  if (phrases.length === 0) return null
  return (
    <ul className="fim-reserves">
      {phrases.map((p) => <li key={p} className="fi-reserve">{p}</li>)}
    </ul>
  )
}

function PeriodesLues({ lot }: { lot: LotDetail }) {
  const nuits = Array.isArray(lot.summary['nuits']) ? (lot.summary['nuits'] as unknown[]) : []
  if (nuits.length === 0) {
    const dates = [...new Set(lot.lignes.map((l) => l.business_date).filter((d): d is string => d !== null))]
    if (dates.length === 0) return <p className="fr-mini">Aucune période n’est rattachée pour l’instant.</p>
    return (
      <p className="fr-mini">
        {dates.length} nuit(s) identifiée(s), de {dates[0]} à {dates[dates.length - 1]}.
      </p>
    )
  }
  return (
    <div className="fi-tablewrap">
      <table className="fi-table fim-periodes">
        <thead>
          <tr>
            <th scope="col">Feuille</th>
            <th scope="col">Nuit</th>
            <th scope="col">Points de vente ouverts</th>
          </tr>
        </thead>
        <tbody>
          {nuits.map((n, i) => {
            if (!estObjet(n)) return null
            return (
              <tr key={texte(n['feuille']) ?? String(i)}>
                <td>{texte(n['feuille']) ?? '—'}</td>
                <td>{texte(n['business_date']) ?? 'Non identifiée'}</td>
                <td>{entier(n['points_de_vente_ouverts']) ?? 0}</td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

function TableApercu({ lignes }: { lignes: LigneLot[] }) {
  if (lignes.length === 0) {
    return (
      <div className="fi-empty">
        <p className="fi-empty__title">Ce fichier ne contient aucune ligne exploitable.</p>
      </div>
    )
  }
  return (
    <div className="fi-tablewrap">
      <table className="fi-table fim-apercu">
        <thead>
          <tr>
            <th scope="col">Origine</th>
            <th scope="col">Nuit</th>
            <th scope="col">Point de vente</th>
            <th scope="col" className="fi-money">Source</th>
            <th scope="col" className="fi-money">Constaté</th>
            <th scope="col" className="fi-money">Écart</th>
            <th scope="col">Confiance</th>
          </tr>
        </thead>
        <tbody>
          {lignes.map((l) => {
            const c = CONFIANCES[l.confiance]
            return (
              <tr key={l.id} className={l.conflit !== null ? 'fim-row--conflit' : undefined}>
                <td className="fim-code">{l.origine}</td>
                <td>{l.business_date ?? '—'}</td>
                <td>{texte(l.payload['point_de_vente_herite']) ?? texte(l.payload['libelle']) ?? '—'}</td>
                <td className="fi-money">{montantOuTiret(l.payload['source_cents'])}</td>
                <td className="fi-money">{montantOuTiret(l.payload['constate_cents'])}</td>
                <td className="fi-money">{montantOuTiret(l.payload['ecart_cents'])}</td>
                <td><span className={`lc-pill ${c.pill}`}>{c.label}</span></td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

/** Un montant absent rend « — », jamais « 0,00 € » : ne pas savoir n'est pas valoir zéro. */
function montantOuTiret(v: unknown): string {
  const n = cents(v)
  return n === null ? '—' : formatMoney(n)
}

function estFaite(cle: Etape, courante: Etape): boolean {
  return ETAPES.findIndex((e) => e.cle === cle) < ETAPES.findIndex((e) => e.cle === courante)
}
